import { useEffect } from 'react';

/**
 * Custom hook to lock the test screen during active assessments
 * - Prevents navigation away from test
 * - Hides any menu/footer elements that might be visible
 * - ALLOWS page scrolling within the test container
 * - Applies only when isActive is true
 */
const useTestScreenLock = (isActive) => {
  useEffect(() => {
    if (!isActive) return;

    // Store original scroll position
    const scrollY = window.scrollY;
    
    // Add class to body for CSS targeting
    document.body.classList.add('test-screen-locked');
    
    // Hide any navigation elements (external to test)
    const hideElements = [
      'header:not([data-test-header])',
      'nav:not([data-test-nav])',
      'footer',
      '.site-header',
      '.site-footer',
      '.main-nav',
      '.global-nav',
      '#main-header',
      '#main-footer'
    ];
    
    // Create style element for hiding external elements and enabling scroll in test container
    const styleEl = document.createElement('style');
    styleEl.id = 'test-screen-lock-styles';
    styleEl.innerHTML = `
      /* Hide external navigation elements during test */
      body.test-screen-locked ${hideElements.join(', body.test-screen-locked ')} {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      
      /* Allow body to scroll normally */
      body.test-screen-locked {
        overflow: auto !important;
        height: auto !important;
        position: static !important;
      }
      
      /* Ensure html also allows scrolling */
      html {
        overflow: auto !important;
        height: auto !important;
      }
      
      /* Test container fills viewport but allows scroll */
      body.test-screen-locked .test-screen-container {
        min-height: 100vh !important;
        position: relative !important;
        z-index: 9999 !important;
        overflow: visible !important;
      }
      
      /* Ensure scrollable areas inside the test work properly */
      body.test-screen-locked .test-screen-container .overflow-y-auto,
      body.test-screen-locked .test-screen-container .overflow-auto {
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
      }
      
      /* Fix for flex containers - ensure they can grow */
      body.test-screen-locked .test-screen-container .flex-1 {
        flex: 1 1 auto !important;
        min-height: 0 !important;
      }
      
      /* Allow content to expand naturally */
      body.test-screen-locked .test-screen-container .min-h-screen {
        min-height: 100vh !important;
      }
      
      /* Ensure flex column containers allow scrolling */
      body.test-screen-locked .test-screen-container.flex.flex-col {
        height: auto !important;
        min-height: 100vh !important;
      }
    `;
    
    document.head.appendChild(styleEl);
    
    // Scroll to top when test starts
    window.scrollTo(0, 0);
    
    // Prevent keyboard shortcuts that might navigate away
    const handleKeyDown = (e) => {
      // Block Alt+Tab indicator (we can't actually prevent it, but block the visual)
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault();
      }
      // Block F5 refresh
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault();
      }
      // Block Ctrl+W close tab
      if (e.ctrlKey && e.key === 'w') {
        e.preventDefault();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup function
    return () => {
      // Remove class
      document.body.classList.remove('test-screen-locked');
      
      // Remove style element
      const existingStyle = document.getElementById('test-screen-lock-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      // Remove event listener
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isActive]);
};

export default useTestScreenLock;
